import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { component } from 'Helpers/PropTypes';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import MuiList from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/List';
import useListDetailActions from 'Components/UI/ListDetailView/useListDetailActions';
import { useSelectionContextState } from 'Components/UI/ListDetailView/SelectionContext';
import { FDBox, FDEmptyState } from '@facet-wealth/design';
import {
  useListContextState,
} from '../ListContext';
import ListSkeletons from './ListSkeletons';
import ListItemSkeleton from './ListItemSkeleton';

const styles = theme => ({
  root: {},
  listItemGrayBackground: {
    backgroundColor: theme.palette.facet.lightGray,
  },
  listItemWhiteBackground: {
    backgroundColor: theme.palette.common.white,
  },
});

const List = ({
  className,
  classes,
  showSkeletonAvatar,
  showAlternatingRows,
  explicitlyWhiteBackground,
  highlightSelected,
  style,
  ListItemComponent,
  ListItemComponentProps,
  emptyStateTitle,
  emptyStateIcon,
  ...rest
}) => {
  const { selectResource } = useListDetailActions();
  const listState = useListContextState();
  const { collection, resources, loading } = listState;
  const selectionState = useSelectionContextState();
  const { selectedResourceSelfLink } = selectionState;

  const hasResources = resources ? resources.length > 0 : false;

  const listContent = hasResources ? resources.map((resource, i) => {
    const resourceSelfLink = resource.link('self').url();

    return (
      <ListItemComponent
        className={classNames({
          [classes.listItemGrayBackground]: showAlternatingRows && i % 2 === 1,
          [classes.listItemWhiteBackground]: explicitlyWhiteBackground && (!showAlternatingRows || i % 2 === 0),
        })}
        key={resource.link('self').url()}
        onClick={() => {
          selectResource(resource, collection);
        }}
        resource={resource}
        selected={highlightSelected && resourceSelfLink === selectedResourceSelfLink}
        {...ListItemComponentProps}
      />
    );
  }) : (
    <FDBox margin="400">
      <FDEmptyState title={emptyStateTitle} icon={emptyStateIcon} />
    </FDBox>
  );

  return (
    <MuiList
      className={classNames(classes.root, className)}
      style={style}
      {...rest}
    >
      {loading ? (
        <ListSkeletons
          showGrayBackground={showAlternatingRows}
          component={ListItemSkeleton}
          componentProps={{
            showAvatar: showSkeletonAvatar,
          }}
        />
      ) : listContent}
    </MuiList>
  );
};

List.defaultProps = {
  className: '',
  showSkeletonAvatar: false,
  explicitlyWhiteBackground: false,
  showAlternatingRows: true,
  style: {},
  ListItemComponentProps: {},
  emptyStateTitle: 'No items in list',
  emptyStateIcon: 'list',
};

List.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  explicitlyWhiteBackground: PropTypes.bool,
  highlightSelected: PropTypes.bool.isRequired,
  showSkeletonAvatar: PropTypes.bool,
  showAlternatingRows: PropTypes.bool,
  style: PropTypes.object,
  ListItemComponent: component.isRequired,
  ListItemComponentProps: component,
  emptyStateTitle: PropTypes.string,
  emptyStateIcon: PropTypes.string,
};

export default withStyles(styles)(List);
